import axios from "axios";
import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactGA from "react-ga4";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import "./App.css";
import Domain from "./components/Courses/Domain";
import SubtopicPage from "./components/Courses/SubtopicPage";
import MainLayout from "./components/dashboardNew/DashBoardLayout";
import AccountSettings from "./components/dashboardNew/pages/AccountSetting";
import ForgotPassword from "./components/Login/ForgotPassword";
import ResetPassword from "./components/Login/ResetPassword";
import Nopage from "./components/nopage/Nopage";
import NotesPage from "./components/Notes/NotesPage";
import PackPage from "./components/Packs/PacksPage";
import PracticeTestMain from "./components/practicetest/PracticeTestMain";
import PrepModules from "./components/prepmodules/PrepModules";
import TestPage from "./components/Test/TestPage";
import AboutPage from "./pages/Aboutpage";
import Coursespage from "./pages/Coursespage";
import EligibilityPage from "./pages/EligibilityPage";
import Homepage from "./pages/Homepage";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import Syllabus from "./pages/Syllabus";
import University from "./pages/UniversityPage";
import PrivateRoutes from "./utils/PrivateRoutes";
// import DashBoardNew from "./components/dashboardNew/pages/Dashboard";
import Cards from "./components/dashboardNew/Cards/Cards";
import UserPlans from "./components/dashboardNew/pages/UserPlans";
import BoughtPack from "./pages/BoughtPack";
// import PriceTable from "./components/Packs/PriceCard";
import Blog from "./pages/Blog";

import { useAuth } from "./utils/context";

import BlogStructure from "./components/BlogCard/Structure";
import CoursesNew from "./components/Courses/CoursesNew";
// import Modal from "./components/home/Modal";
import { MathJaxContext } from "better-react-mathjax";
import CancellationRefundPolicy from "./pages/Cancellation";
import Contact from "./pages/Contact";
import PrivacyPolicy from "./pages/Privacy";
import ShippingPolicy from "./pages/Shipping";
import TermsAndConditions from "./pages/TermsAndConditions";

import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import "primeicons/primeicons.css";
import AdminDashboard from "./components/admin/AdminDashboard";
import DashboardOverview from "./components/dashboardNew/pages/DashboardOverview";
import StudentsList from "./components/dashboardNew/pages/StudentsList";
import UserPerformance from "./components/dashboardNew/pages/UserPerformance";
import UserReports from "./components/dashboardNew/pages/UserReports";
import ImpersonationBanner from "./components/session/ImpersonationBanner";
import SessionExpiredPopup from "./components/session/SessionExpiredPopup";
import Coupons from "./pages/Coupons";
import {
  revokeServerSession,
  shouldSuppressSessionExpiryPopup,
} from "./services/revokeServerSession";
import { API } from "./utils/constants";
import DisableJs from "./utils/disableJs";
import { SearchProvider } from "./utils/SearchProvider";

const isAdminUser = (user) =>
  user?.isAdmin === true || user?.role === "admin";

const config = {
  loader: { load: ["input/tex", "output/chtml"] },
  tex: {
    inlineMath: [
      ["$", "$"],
      ["\\(", "\\)"],
    ],
    displayMath: [
      ["$$", "$$"],
      ["\\[", "\\]"],
    ],
  },
};

const App = () => {
  const [auth, setAuth] = useAuth();
  const selectedSubjects = auth?.user?.selectedSubjects;
  const navigate = useNavigate();
  const location = useLocation();
  const [isSessionValidated, setIsSessionValidated] = useState(
    !auth?.accessToken?.token,
  );
  const [verifiedIsAdmin, setVerifiedIsAdmin] = useState(false);

  // const [showFirstModal, setShowFirstModal] = useState(false);
  // const [showSecondModal, setShowSecondModal] = useState(false);

  // useEffect(() => {
  //   const isExcludedPath =
  //     ["/login", "/signup", "/forgot-password", "/purchase"].includes(
  //       window.location.pathname
  //     ) || window.location.pathname.startsWith("/reset_password/");

  //   if (!isExcludedPath) {
  //     const firstModalTimer = setTimeout(() => {
  //       setShowFirstModal(true);
  //     }, 2 * 1000);

  //     const secondModalTimer = setTimeout(() => {
  //       setShowSecondModal(true);
  //     }, 5 * 60 * 1000);

  //     return () => {
  //       clearTimeout(firstModalTimer);
  //       clearTimeout(secondModalTimer);
  //     };
  //   }
  // }, []);

  // const closeModal = () => {
  //   setShowFirstModal(false);
  //   setShowSecondModal(false);
  // };

  const [showSessionExpiredPopup, setShowSessionExpiredPopup] = useState(false);

  const restoreAdminSessionFromBackup = useCallback(async () => {
    const adminToken = localStorage.getItem("adminTokenBackup");
    const adminUserStr = localStorage.getItem("adminUserBackup");

    if (!adminToken || !adminUserStr) {
      return false;
    }

    try {
      const response = await fetch(`${API}/auth/validate-session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
      });

      if (!response.ok) {
        throw new Error("Admin backup session is invalid.");
      }

      const data = await response.json();
      const adminUser = data?.user;
      const isValidatedAdmin =
        adminUser?.isAdmin === true || adminUser?.role === "admin";

      if (!isValidatedAdmin) {
        throw new Error("Backup session does not belong to an admin.");
      }

      const restoredAuth = {
        user: adminUser,
        accessToken: {
          token: adminToken,
          expiry: Date.now() + 24 * 60 * 60 * 1000,
        },
      };

      setAuth(restoredAuth);
      localStorage.setItem("auth", JSON.stringify(restoredAuth));
      localStorage.removeItem("adminTokenBackup");
      localStorage.removeItem("adminUserBackup");
      localStorage.removeItem("isImpersonating");
      navigate("/admin-dashboard", { replace: true });
      return true;
    } catch (restoreError) {
      console.error(
        "Failed to restore admin session from backup",
        restoreError,
      );
      localStorage.removeItem("adminTokenBackup");
      localStorage.removeItem("adminUserBackup");
      localStorage.removeItem("isImpersonating");
      return false;
    }
  }, [navigate, setAuth]);

  const handleLogout = async () => {
    await revokeServerSession(auth?.accessToken);
    setAuth({
      ...auth,
      user: null,
      accessToken: "",
    });
    localStorage.removeItem("auth");
    setShowSessionExpiredPopup(false);
    navigate("/login");
  };

  const handleSharedSessionIssue = useCallback(async () => {
    if (shouldSuppressSessionExpiryPopup()) {
      return;
    }

    if (localStorage.getItem("isImpersonating") === "true") {
      const restored = await restoreAdminSessionFromBackup();
      if (restored) {
        return;
      }
    }

    setShowSessionExpiredPopup(true);
  }, [restoreAdminSessionFromBackup]);

  useEffect(() => {
    const setupAxiosDefaults = () => {
      if (auth?.accessToken?.token) {
        axios.defaults.headers.common["Authorization"] =
          `Bearer ${auth.accessToken.token}`;
      } else {
        delete axios.defaults.headers.common["Authorization"];
      }
    };

    // Initial setup
    setupAxiosDefaults();

    const reqInterceptor = axios.interceptors.request.use(
      (config) => {
        if (auth?.accessToken?.token) {
          config.headers.Authorization = `Bearer ${auth.accessToken.token}`;
        }
        return config;
      },
      (error) => Promise.reject(error),
    );

    const resInterceptor = axios.interceptors.response.use(
      (response) => {
        if (response?.data?.isLoggedInElsewhere) {
          handleSharedSessionIssue();
        }
        return response;
      },
      async (error) => {
        if (shouldSuppressSessionExpiryPopup()) {
          return Promise.reject(error);
        }

        // Impersonation 401 handler
        if (
          error.response?.status === 401 &&
          localStorage.getItem("isImpersonating") === "true"
        ) {
          console.log("Impersonation token expired. Restoring admin session.");
          const restored = await restoreAdminSessionFromBackup();
          if (restored) {
            return Promise.reject(error);
          }
        }

        const msg =
          (typeof error.response?.data?.message === "string" &&
            error.response.data.message) ||
          (typeof error.response?.data?.msg === "string" &&
            error.response.data.msg) ||
          "";

        const isSharedSessionAuthIssue =
          msg === "Session expired. Please logout and login again." ||
          msg === "Session invalid. Please login again." ||
          msg === "Session invalid. You have logged in from another device." ||
          msg === "Invalid authentication token." ||
          msg === "Invalid token format.";

        if (error.response && isSharedSessionAuthIssue) {
          console.log("Session expired detected by interceptor");
          await handleSharedSessionIssue();
        }
        return Promise.reject(error);
      },
    );

    return () => {
      axios.interceptors.request.eject(reqInterceptor);
      axios.interceptors.response.eject(resInterceptor);
    };
  }, [
    auth?.accessToken?.token,
    handleSharedSessionIssue,
    restoreAdminSessionFromBackup,
  ]);

  useEffect(() => {
    if (auth?.accessToken?.token) {
      axios.defaults.headers.common["Authorization"] =
        `Bearer ${auth.accessToken.token}`;
    } else {
      delete axios.defaults.headers.common["Authorization"];
    }
  }, [auth?.accessToken?.token]);

  // Keep the latest callbacks in refs so the session-validation effect below
  // can depend ONLY on the auth token. Without this, handleSharedSessionIssue /
  // setAuth identities change on render (e.g. on every route change), which
  // previously caused validate-session to fire 2-3 times per navigation.
  const handleSharedSessionIssueRef = useRef(handleSharedSessionIssue);
  const setAuthRef = useRef(setAuth);
  useEffect(() => {
    handleSharedSessionIssueRef.current = handleSharedSessionIssue;
    setAuthRef.current = setAuth;
  }, [handleSharedSessionIssue, setAuth]);

  // Background re-validation ping (detects logins from other devices).
  // Only the periodic interval remains — the original initial setTimeout call
  // was removed because it duplicated the validateCurrentSession call below
  // (the source of validate-session firing twice, with a 300ms delay).
  useEffect(() => {
    if (auth?.accessToken?.token) {
      const validateSession = () => {
        axios
          .post(`${API}/auth/validate-session`, {})
          .then((response) => {
            if (response?.data?.isLoggedInElsewhere) {
              handleSharedSessionIssueRef.current();
            }
          })
          .catch((error) => {
            console.log("Session validation ping completed");
          });
      };

      const intervalId = setInterval(validateSession, 30000);

      return () => {
        clearInterval(intervalId);
      };
    }
  }, [auth?.accessToken?.token]);

  useEffect(() => {
    let isMounted = true;

    const validateCurrentSession = async () => {
      if (!auth?.accessToken?.token) {
        if (isMounted) {
          setVerifiedIsAdmin(false);
          setIsSessionValidated(true);
        }
        return;
      }

      if (isMounted) {
        setIsSessionValidated(false);
      }

      try {
        const response = await axios.post(`${API}/auth/validate-session`, {});

        if (response?.data?.isLoggedInElsewhere) {
          await handleSharedSessionIssueRef.current();
          return;
        }

        const serverUser = response?.data?.user;

        if (!isMounted) return;

        const nextIsAdmin = isAdminUser(serverUser);
        setVerifiedIsAdmin(nextIsAdmin);

        if (serverUser) {
          setAuthRef.current((currentAuth) => {
            if (
              JSON.stringify(serverUser) === JSON.stringify(currentAuth?.user)
            ) {
              return currentAuth;
            }

            const refreshedAuth = {
              ...currentAuth,
              user: serverUser,
            };
            localStorage.setItem("auth", JSON.stringify(refreshedAuth));
            return refreshedAuth;
          });
        }
      } catch (error) {
        if (!isMounted) return;
        setVerifiedIsAdmin(false);
      } finally {
        if (isMounted) {
          setIsSessionValidated(true);
        }
      }
    };

    validateCurrentSession();

    return () => {
      isMounted = false;
    };
  }, [auth?.accessToken?.token]);

  useEffect(() => {
    const processAuthFromQuery = async () => {
      const params = new URLSearchParams(location.search);
      const token = params.get("token");
      const userId = params.get("userId");

      if (token && userId) {
        try {
          const response = await axios.get(`${API}/users/find/${userId}`);
          if (response.data) {
            const userData = response.data;

            // Create proper accessToken object structure with token and expiry
            const currentTime = Date.now();
            const expiryTime = currentTime + 24 * 60 * 60 * 1000; // 24 hours from now

            const newAuthData = {
              user: userData,
              accessToken: {
                token: token,
                expiry: expiryTime,
              },
            };

            setAuth(newAuthData);
            localStorage.setItem("auth", JSON.stringify(newAuthData));

            // Land on home so URL matches logged-in state (avoids /login + refresh flash).
            navigate("/", { replace: true });
          } else {
            console.error(
              "API call successful but no user data received for ID:",
              userId,
            );
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
        }
      }
    };

    processAuthFromQuery();
  }, [location.search, setAuth, navigate]);

  useEffect(() => {
    ReactGA.send({
      hitType: "pageview",
      page: window.location.pathname + window.location.search,
      title: "Current Page",
    });
  }, []);

  const PrepModulesWithDisableJs = () => {
    return (
      <>
        <DisableJs />
        <PrepModules />
      </>
    );
  };

  const PracticeTestMainWithDisableJs = () => {
    return (
      <>
        <DisableJs />
        <PracticeTestMain />
      </>
    );
  };

  return (
    <>
      <SearchProvider>
        <MathJaxContext version={3} config={config}>
          {showSessionExpiredPopup && (
            <SessionExpiredPopup onClose={handleLogout} />
          )}
          <ImpersonationBanner />
          <Routes>
            <Route
              path="/login"
              element={!auth?.user ? <Login /> : <Navigate to="/" replace />}
            />
            <Route
              path="/signup"
              element={!auth?.user ? <Signup /> : <Navigate to="/" replace />}
            />
            <Route
              path="/forgot-password"
              element={
                !auth?.user ? <ForgotPassword /> : <Navigate to="/" replace />
              }
            />
            <Route
              path="/profile"
              element={
                auth?.user ? <MainLayout /> : <Navigate to="/login" replace />
              }
            >
              <Route index element={<DashboardOverview />} />
              <Route path="performance" element={<UserPerformance />} />
              <Route path="students-list" element={<StudentsList />} />
              <Route path="settings" element={<AccountSettings />} />
              <Route path="reports" element={<UserReports />} />

              <Route index element={<AccountSettings />} />
              <Route path="progress" element={<Cards />} />
              <Route path="plans" element={<UserPlans />} />
              {/* <Route path="plans" element={<PriceTable hidden="hidden" />} /> */}
            </Route>
            <Route
              path="/reset_password/:id/:token/:expire"
              element={!auth?.user ? <ResetPassword /> : <Homepage />}
            />
            <Route
              path="/courses"
              element={!auth?.user ? <Coursespage /> : <CoursesNew />}
            />
            <Route path="/Domain" element={<Domain />} />
            <Route
              path="/"
              element={
                auth?.user && !isSessionValidated ? null : selectedSubjects &&
                  selectedSubjects.length > 0 ? (
                  <BoughtPack />
                ) : (
                  <Homepage />
                )
              }
            />{" "}
            <Route path="/contact" element={<Contact />} />
            <Route path="/coupons" element={<Coupons />} />
            <Route
              path="/terms-and-conditions"
              element={<TermsAndConditions />}
            />
            <Route
              path="cancellation-and-refund"
              element={<CancellationRefundPolicy />}
            />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/shipping-policy" element={<ShippingPolicy />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/blogs" element={<Blog />} />
            <Route path="/blogs/:topic" element={<BlogStructure />} />
            <Route path="/syllabus" element={<Syllabus />} />
            <Route path="/purchase" element={<PackPage />} />
            <Route path="/university" element={<University />} />
            <Route
              path="/admin-dashboard"
              element={
                !auth?.user ? (
                  <Navigate to="/login" replace />
                ) : auth?.user &&
                  !isSessionValidated ? null : verifiedIsAdmin ? (
                  <AdminDashboard />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route
              path="/cuet-course-eligibility"
              element={<EligibilityPage />}
            />
            <Route path="/courses/:topic" element={<TestPage />} />
            <Route
              path="/courses/:topic/:testCategory"
              element={<TestPage />}
            />
            <Route
              path="/courses/notes/:topic/:testCategory/:subTopic"
              element={<NotesPage />}
            />
            <Route
              path="/courses/notes/:topic/:subTopic"
              element={<NotesPage />}
            />
            <Route path="/courses/domain/:heading" element={<SubtopicPage />} />
            <Route
              path="/courses/prep/:subject/:topic/:subTopic"
              element={<PrepModulesWithDisableJs />}
            />
            <Route
              path="/courses/prep/:topic/:subTopic"
              element={<PrepModulesWithDisableJs />}
            />
            <Route
              path="/courses/practice/:topic/:subTopic"
              element={<PracticeTestMainWithDisableJs />}
            />
            <Route element={<PrivateRoutes />}>
              <Route
                path="/courses/practice/:subTopic"
                element={<PracticeTestMainWithDisableJs />}
              />
            </Route>
            <Route path="*" element={<Nopage />} />
          </Routes>
          {/* {showFirstModal && (
        <Modal
          isOpen={showFirstModal}
          onClose={closeModal}
          imageSrc={require("./assets/images/banner02.jpeg")}
        />
      )}
      {!showFirstModal && showSecondModal && (
        <Modal
          isOpen={showSecondModal}
          onClose={closeModal}
          imageSrc={require("./assets/images/Banner01.png")}
        />
      )} */}
        </MathJaxContext>
      </SearchProvider>
      <Analytics />
      <SpeedInsights />
    </>
  );
};

export default App;
